"use client";
import type { ReactNode } from "react";

import { AlbumGridItem } from "@/components/album-grid-item";
import { type LinkTarget } from "@/components/link";
import type {
  OfferSummary,
  OfferSummaryBadgeMode,
  OfferSummaryBadgeVariant,
} from "@/components/marketplace/offer-summary-badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { ListingMap } from "@/hooks/use-listing-counts";
import type { AddToListsTarget } from "@/lib/add-to-lists-target";
import { defaultIfUndefined } from "@garimpa/lib/defaultIfUndefined";
import type { OfferSummaryByCondition } from "@garimpa/lib/offerConditions";

export interface AlbumGridAlbum {
  id: string;
  workId?: string | null;
  title: string;
  imageUrl: string | null;
  releaseId?: string | null;
  addToListsTarget?: AddToListsTarget;
  artistDisplay?: string | null;
  year?: number | null;
  type?: string | null;
  offerSummary?: OfferSummary | null;
  offerSummaryByCondition?: OfferSummaryByCondition<OfferSummary>;
}

const GRID_CLASSES = "grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5";

function defaultSubtitle(album: AlbumGridAlbum): string | undefined {
  return [album.artistDisplay, album.year].filter(Boolean).join(" \u00b7 ") || undefined;
}

function defaultLink(album: AlbumGridAlbum): LinkTarget {
  return { to: "/work/$id", params: { id: album.id } };
}

interface AlbumGridProps<TAlbum extends AlbumGridAlbum = AlbumGridAlbum> {
  albums: TAlbum[];
  className?: string;
  itemKey?: (album: TAlbum) => string;
  subtitle?: (album: TAlbum) => string | undefined;
  link?: (album: TAlbum) => LinkTarget;
  tags?: (album: TAlbum) => ReactNode;
  footer?: (album: TAlbum) => ReactNode;
  itemClassName?: string | ((album: TAlbum) => string | undefined);
  listingMap?: ListingMap;
  priorityCount?: number;
  imageSizes?: string;
  isLoading?: boolean;
  skeletonCount?: number;
  emptyState?: ReactNode;
  offerSummaryMode?: OfferSummaryBadgeMode;
  offerSummaryVariant?: OfferSummaryBadgeVariant;
  hideActions?: boolean;
  linkCoversFooter?: boolean;
  showAlbumType?: boolean;
  showOfferSummary?: boolean;
  children?: ReactNode;
}

export function AlbumGrid<TAlbum extends AlbumGridAlbum = AlbumGridAlbum>(
  props: AlbumGridProps<TAlbum>,
) {
  const { albums, isLoading, footer, emptyState, children } = props;
  const className = defaultIfUndefined(props.className, GRID_CLASSES);
  const itemKey = defaultIfUndefined(props.itemKey, (album: TAlbum) => album.id);
  const subtitle = defaultIfUndefined(props.subtitle, defaultSubtitle);
  const link = defaultIfUndefined(props.link, defaultLink);
  const priorityCount = defaultIfUndefined(props.priorityCount, 0);
  const skeletonCount = defaultIfUndefined(props.skeletonCount, 15);
  const linkCoversFooter = defaultIfUndefined(props.linkCoversFooter, false);
  const hideActions = defaultIfUndefined(props.hideActions, false);
  const showAlbumType = defaultIfUndefined(props.showAlbumType, true);
  const showOfferSummary = defaultIfUndefined(props.showOfferSummary, true);
  if (isLoading) {
    return (
      <ul className={className}>
        {Array.from({ length: skeletonCount }, (_, i) => (
          <li className="overflow-hidden rounded-xl border-2 border-transparent bg-card" key={i}>
            <Skeleton className="aspect-square w-full rounded-none" />
            <div className="space-y-2 px-2 pb-2 pt-3">
              <Skeleton className="h-5 w-5/6 rounded-md" />
              <Skeleton className="h-4 w-1/3 rounded-md" />
              {footer && (
                <div className="space-y-2 pt-1">
                  <Skeleton className="h-3 w-4/5 rounded-md" />
                  <Skeleton className="h-[76px] w-full rounded-xl" />
                  <Skeleton className="h-12 w-full rounded-lg" />
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  if (albums.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <>
      <ul className={className}>
        {albums.map((album, index) => (
          <AlbumGridItem
            key={itemKey(album)}
            album={album}
            footer={footer}
            hideActions={hideActions}
            index={index}
            imageSizes={props.imageSizes}
            itemClassName={props.itemClassName}
            link={link}
            linkCoversFooter={linkCoversFooter}
            listingMap={props.listingMap}
            offerSummaryMode={props.offerSummaryMode}
            offerSummaryVariant={props.offerSummaryVariant}
            priorityCount={priorityCount}
            showAlbumType={showAlbumType}
            showOfferSummary={showOfferSummary}
            subtitle={subtitle}
            tags={props.tags}
          />
        ))}
      </ul>
      {children}
    </>
  );
}
